import {
  Component,
  inject,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { Bot } from '@shared/models';
import {
  MatCard,
  MatCardHeader,
  MatCardTitle,
  MatCardSubtitle,
  MatCardActions,
  MatCardContent,
  MatCardFooter,
} from '@angular/material/card';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDivider } from '@angular/material/divider';
import { MatButton } from '@angular/material/button';
import { MatBadge } from '@angular/material/badge';
import { DeviceIconPipe } from '../../pipes/device-icon.pipe';
import { CrawlerErrorService } from '@core/services/crawler-error.service';
import { HeatmapDay } from '../error-heatmap/error-heatmap.component';
import { catchError, of } from 'rxjs';

interface IncidentCounts {
  threeDays: number;
  sevenDays: number;
  oneMonth: number;
}

@Component({
  selector: 'app-dashboard-bot-thumb',
  templateUrl: './dashboard-bot-thumb.component.html',
  styleUrls: ['./dashboard-bot-thumb.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardSubtitle,
    MatCardActions,
    MatIcon,
    MatTooltip,
    MatCardContent,
    MatDivider,
    MatCardFooter,
    MatButton,
    MatBadge,
    DeviceIconPipe,
  ],
})
export class DashboardBotThumbComponent implements OnInit {
  @Input() bot: Bot;

  protected incidentCounts: IncidentCounts = {
    threeDays: 0,
    sevenDays: 0,
    oneMonth: 0,
  };

  private readonly router = inject(Router);

  private readonly crawlerErrorService = inject(CrawlerErrorService);

  ngOnInit(): void {
    this.crawlerErrorService
      .getCrawlerErrorsHeatmap({ botId: this.bot.id })
      .pipe(catchError(() => of([] as HeatmapDay[][])))
      .subscribe((heatmap) => {
        this.incidentCounts = this.computeIncidentCounts(heatmap);
      });
  }

  navigateToBot(): void {
    const langSet = this.router.url.split('/')[1];
    const isProdMode = ['fr', 'en'].includes(langSet);
    if (isProdMode) {
      this.router.navigate(['/', langSet, 'bots', this.bot.id]);
      return;
    }

    this.router.navigate(['/', 'bots', this.bot.id]);
  }

  private computeIncidentCounts(heatmap: HeatmapDay[][]): IncidentCounts {
    const days = heatmap.flat();
    const now = new Date();

    const sumSinceDaysAgo = (daysAgo: number): number => {
      const threshold = new Date(now);
      threshold.setDate(threshold.getDate() - (daysAgo - 1));
      threshold.setHours(0, 0, 0, 0);

      return days
        .filter((day) => {
          const date = new Date(day.date);
          return date >= threshold && date <= now;
        })
        .reduce((total, day) => total + day.count, 0);
    };

    return {
      threeDays: sumSinceDaysAgo(3),
      sevenDays: sumSinceDaysAgo(7),
      oneMonth: sumSinceDaysAgo(30),
    };
  }
}
